export const LEGAL_DOCS = {
  "privacy-policy": {
    title: "Privacy Policy",
    updated: "[Last updated — editable placeholder]",
    intro:
      "Upgrade Law ('we', 'the practice') respects the privacy of everyone who visits this website or submits an enquiry. This policy explains what we collect, why, and the choices you have. It is an editable draft prepared for review before publication.",
    sections: [
      {
        h: "1. What we collect",
        p: ["When you submit the legal enquiry form, we collect the details you provide: your name, email address, phone number, the service category you select, and your enquiry description. We also record a technical reference of your connection (a one-way hashed IP address) to prevent spam and abuse."],
      },
      {
        h: "2. Why we collect it",
        p: [
          "Your enquiry details are used solely to review and respond to your request for legal assistance. The hashed connection reference is used only for security and spam prevention. We do not sell, rent or trade your information, and we do not use it for unsolicited marketing.",
        ],
      },
      {
        h: "3. Consent and withdrawal",
        p: [
          "We process enquiry details on the basis of your consent, given through the consent checkbox on the form, consistent with India's Digital Personal Data Protection Act, 2023. You may withdraw consent or ask us to delete your enquiry at any time by writing to us — we will act on verified requests within a reasonable period.",
        ],
      },
      {
        h: "4. Cookies and analytics",
        p: [
          "This website uses essential cookies and local storage to function. Analytics tools (such as Google Analytics) are loaded only if you accept them through the cookie banner, and only if such tools are enabled by the site owner. If you decline, no analytics cookies are set. Your choice is stored in your browser and can be revisited by clearing site data.",
        ],
      },
      {
        h: "5. Storage and security",
        p: [
          "Enquiry submissions are stored in a secured database with access limited to the practice. We apply reasonable technical and organisational safeguards appropriate to the sensitivity of the data. No system is perfectly secure, so please do not send confidential case documents, identity documents, financial records or passwords through this website.",
        ],
      },
      {
        h: "6. Retention",
        p: [
          "Enquiry records are retained for as long as needed to respond to your request and for any period required by applicable professional or legal obligations, after which they are deleted or anonymised.",
        ],
      },
      {
        h: "7. Your rights",
        p: [
          "Subject to applicable law, you may ask us for access to the personal data we hold about you, request correction or erasure, or raise a grievance about its handling. Write to the contact email on this website and mark your message 'Data request'.",
        ],
      },
      {
        h: "8. Third-party services",
        p: [
          "This website may link to third-party services (such as WhatsApp, Google Maps or search engines) whose own privacy policies apply once you follow those links. We encourage you to read them.",
        ],
      },
      {
        h: "9. Changes to this policy",
        p: [
          "We may update this policy from time to time. The latest version will always appear on this page. [Grievance officer name and contact details to be added before publication.]",
        ],
      },
    ],
  },
  "terms-of-use": {
    title: "Terms of Use",
    updated: "[Last updated — editable placeholder]",
    intro:
      "These terms govern your use of this website. By browsing it, you agree to them. They are an editable draft prepared for review before publication.",
    sections: [
      {
        h: "1. Nature of the website",
        p: [
          "This website provides general information about Upgrade Law and the areas of law it works in. Nothing on this website — including articles, service descriptions and the enquiry form — is legal advice, and nothing on it creates an advocate-client relationship.",
        ],
      },
      {
        h: "2. Intellectual property",
        p: [
          "The name, logo, text and design of this website are the property of the practice unless stated otherwise. You may read, print and share pages for personal, non-commercial reference. You may not reproduce the website or its content commercially, or present it as your own, without written permission.",
        ],
      },
      {
        h: "3. Acceptable use",
        p: [
          "You agree not to misuse this website: no attempts to breach security, submit spam or automated enquiries, extract data systematically, or interfere with its operation. Enquiry submissions that are abusive, unlawful or designed to solicit work will be discarded.",
        ],
      },
      {
        h: "4. Third-party links and services",
        p: [
          "The website links to third-party services such as WhatsApp and may embed Google Maps. We do not control those services and are not responsible for their content, availability or policies.",
        ],
      },
      {
        h: "5. Limitation of liability",
        p: [
          "The website is provided on an 'as is' basis. To the fullest extent permitted by law, the practice is not liable for any loss arising from reliance on the website's content, or from any interruption or error in its availability. Legal advice is given only through a formal engagement.",
        ],
      },
      {
        h: "6. Governing law",
        p: [
          "These terms are governed by the laws of India. Subject to applicable law, the courts at [city — to be added] will have exclusive jurisdiction over disputes arising from the website.",
        ],
      },
      {
        h: "7. Changes",
        p: [
          "We may revise these terms from time to time. Continued use of the website after changes are posted constitutes acceptance of the revised terms.",
        ],
      },
    ],
  },
  disclaimer: {
    title: "Disclaimer",
    updated: "[Last updated — editable placeholder]",
    intro:
      "This disclaimer applies to the entire website. Please read it carefully. It is an editable draft prepared for review before publication.",
    sections: [
      {
        h: "1. Compliance statement",
        p: [
          "In accordance with the rules of the Bar Council of India, advocates are not permitted to solicit work or advertise. By continuing to use this website, you acknowledge that:",
        ],
        list: [
          "You are seeking information about Upgrade Law of your own accord, and there has been no advertisement, personal communication, solicitation or inducement of any kind from the practice or its members.",
          "The purpose of this website is only to provide general information about the practice, its service areas and its approach.",
          "The practice is not offering, and this website does not constitute, legal advice or a promise of any outcome.",
        ],
      },
      {
        h: "2. General information only — not legal advice",
        p: [
          "All content on this website, including articles and service descriptions, is general information about Indian law and legal processes as at the date of publication. It is not legal advice and must not be relied upon as a substitute for advice on your specific facts. Laws change; summaries age. Always consult a qualified legal professional before acting.",
        ],
      },
      {
        h: "3. No advocate-client relationship",
        p: [
          "Browsing this website, reading its content, submitting the enquiry form or communicating with the practice by phone, WhatsApp or email does not create an advocate-client relationship. Such a relationship arises only through a formal, written engagement accepted by the practice. Until then, please do not send confidential information — we may already advise another party whose interests could conflict.",
        ],
      },
      {
        h: "4. No guarantees of results",
        p: [
          "The practice does not guarantee, warrant or predict any outcome in any matter. Any description of past work, if ever added, would not imply that similar results can be obtained in other matters.",
        ],
      },
      {
        h: "5. Placeholder content",
        p: [
          "Some details on this website — including contact information, the office address and the professional profile — are clearly marked editable placeholders pending verification. They will be completed only with accurate, verifiable information before publication.",
        ],
      },
      {
        h: "6. External links",
        p: [
          "Links to external websites are provided for convenience only. The practice does not endorse and is not responsible for the content of third-party sites.",
        ],
      },
      {
        h: "7. Scope of this disclaimer",
        p: [
          "This disclaimer is made in the interest of transparency and does not override, limit or replace any professional, statutory or regulatory obligation that applies to the practice or its members.",
        ],
      },
    ],
  },
};
